import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SEO, { buildCanonicalUrl } from "../../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../../components/Breadcrumbs";
import {
  HUMANOID_GROWTH_END_YEAR,
  HUMANOID_GROWTH_START_YEAR,
  companiesForGrowthRegion,
  cumulativeHumanoidCompanySeries,
  humanoidCompanyGrowthCompanies,
  humanoidGrowthRegions,
  humanoidGrowthTotalsByRegion,
} from "../../data/humanoidCompanyGrowth";

const PATH = "/humanoid-robot-company-growth";
const WIDTH = 1100;
const HEIGHT = 520;
const PAD = { left: 70, right: 34, top: 50, bottom: 62 };

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function chartTitle(region) {
  return `${region}: documented company founding years since 2000`;
}

function useAnimatedNumber(target, reduceMotion) {
  const [value, setValue] = useState(target);
  const previous = useRef(target);

  useEffect(() => {
    const start = previous.current;
    previous.current = target;
    if (reduceMotion || start === target) {
      setValue(target);
      return undefined;
    }

    const startedAt = performance.now();
    const duration = 650;
    let frame;
    const tick = (now) => {
      const progress = clamp((now - startedAt) / duration, 0, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(start + (target - start) * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, reduceMotion]);

  return value;
}

function buildSmoothPath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const midpointX = (previous.x + current.x) / 2;
    d += ` C ${midpointX} ${previous.y}, ${midpointX} ${current.y}, ${current.x} ${current.y}`;
  }
  return d;
}

export default function HumanoidCompanyGrowthPage() {
  const initialRegion = useMemo(() => {
    if (typeof window === "undefined") return "Global";
    const value = new URLSearchParams(window.location.search).get("region");
    const aliases = { usa: "USA", china: "China", japan: "Japan", "south-korea": "South Korea", europe: "Europe" };
    return aliases[value] || "Global";
  }, []);
  const [region, setRegion] = useState(initialRegion);
  const [activeYear, setActiveYear] = useState(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion reduce)");
    const update = () => setReduceMotion(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  const series = useMemo(() => cumulativeHumanoidCompanySeries(region), [region]);
  const regionCompanies = useMemo(() => companiesForGrowthRegion(region), [region]);
  const totals = useMemo(() => humanoidGrowthTotalsByRegion(), []);
  const total = series.at(-1)?.total || 0;
  const animatedTotal = useAnimatedNumber(total, reduceMotion);
  const maxValue = Math.max(4, total);
  const yStep = maxValue <= 8 ? 2 : maxValue <= 20 ? 5 : 10;
  const yScaleMax = Math.ceil(maxValue / yStep) * yStep;
  const plotWidth = WIDTH - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;

  const points = useMemo(
    () =>
      series.map((item, index) => ({
        ...item,
        x: PAD.left + (index / (series.length - 1)) * plotWidth,
        y: PAD.top + plotHeight - (item.total / yScaleMax) * plotHeight,
      })),
    [series, plotWidth, plotHeight, yScaleMax],
  );
  const pathD = useMemo(() => buildSmoothPath(points), [points]);
  const selectedPoint = activeYear == null ? null : points.find((point) => point.year === activeYear);
  const yTicks = useMemo(
    () => Array.from({ length: yScaleMax / yStep + 1 }, (_, i) => i * yStep),
    [yScaleMax, yStep],
  );
  const yearLabels = [2000, 2004, 2008, 2012, 2016, 2020, 2024, 2026];

  const breadcrumbs = [{ label: "Home", to: "/" }, { label: "Humanoid Intelligence", to: "/humanoid-intelligence" }, { label: "Company founding records", to: PATH }];
  const schema = [
    buildBreadcrumbSchema(breadcrumbs),
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Humanoid Companies: Documented Founding Years",
      description: "A curated company-founding timeline with source links and missing-year records. It does not measure market growth or the start of humanoid programmes.",
      url: buildCanonicalUrl(PATH),
      dateModified: "2026-09-29",
    },
    {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: "TechniaHQRobot Company Founding Records",
      description: "A partial collection of companies associated with humanoid platforms. Only documented founding years are counted in the timeline.",
      url: buildCanonicalUrl(PATH),
      temporalCoverage: "2000/2026",
      spatialCoverage: ["China", "United States", "Japan", "South Korea", "Europe"],
      creator: { "@type": "Organization", name: "TechniaHQRobot", url: "https://www.techniahqservice.com/" },
      dateModified: "2026-09-29",
    },
  ];

  return (
    <div className="min-h-screen bg-[#050B17] text-white">
      <SEO
        title="Humanoid Company Founding Years | TechniaHQRobot"
        description="Explore documented founding years for companies associated with humanoid robotics, with source links, missing dates and limits on market comparisons."
        path={PATH}
        schema={schema}
      />
      <Breadcrumbs items={breadcrumbs} />
      <style>{`
        @keyframes growth-line-draw { from { stroke-dashoffset: 1; opacity: .35; } to { stroke-dashoffset: 0; opacity: 1; } }
        @keyframes growth-point-in { from { opacity: 0; transform: scale(.45); } to { opacity: 1; transform: scale(1); } }
        @keyframes growth-pulse { 0%,100% { opacity:.55; transform:scale(1); } 50% { opacity:.12; transform:scale(1.9); } }
      `}</style>

      <section className="mx-auto w-full max-w-[1440px] px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pt-14">
        <div className="max-w-4xl">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-300">TECHNIAHQROBOT HUMANOID INTELLIGENCE</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight sm:text-5xl">Humanoid Companies: Documented Founding Years</h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300 sm:text-base">
            This partial collection groups companies associated with humanoid platforms by the founding years supported by its cited sources. Founding a company and starting a humanoid programme are different events: Tesla and Xiaomi existed before their humanoid projects. The chart does not measure market growth, active suppliers or robot shipments.
          </p>
        </div>

        <section className="mt-7 overflow-hidden border border-[rgba(100,150,255,0.18)] bg-[#0B1428] shadow-[0_30px_90px_rgba(0,0,0,0.28)]">
          <div className="border-b border-white/10 px-4 py-4 sm:px-6">
            <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Humanoid company growth region">
              {humanoidGrowthRegions.map((item) => (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={region === item}
                  onClick={() => {
                    setRegion(item);
                    setActiveYear(null);
                  }}
                  className={`min-h-11 shrink-0 border px-4 text-sm font-black transition focus:outline-none focus:ring-2 focus:ring-cyan-300 ${
                    region === item
                      ? "border-cyan-300/60 bg-cyan-300/10 text-cyan-100"
                      : "border-white/10 bg-white/[0.025] text-slate-400 hover:border-white/25 hover:text-white"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[240px_1fr] lg:items-start">
            <div className="border border-white/10 bg-[#07101f] p-5">
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">CUMULATIVE TOTAL</p>
              <p className="mt-2 text-6xl font-black tabular-nums tracking-[-0.06em] text-white">{animatedTotal}</p>
              <p className="mt-1 text-sm text-slate-400">through 2026</p>
              <div className="mt-5 border-t border-white/10 pt-4 text-xs leading-6 text-slate-500">
                Counts dated records in this collection. Missing dates and companies founded before 2000 are outside the chart. It is not a census.
              </div>
            </div>

            <div>
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-violet-300">2000 → 2026</p>
                  <h2 className="mt-1 text-lg font-black text-white sm:text-xl">{chartTitle(region)}</h2>
                </div>
                <p className="text-xs text-slate-500">{total} dated records; {regionCompanies.filter((company) => !Number.isFinite(company.foundedYear)).length} without a confirmed year</p>
              </div>

              <div
                className="relative overflow-hidden border border-white/10 bg-[#07101f]"
                onClick={() => setActiveYear(null)}
              >
                <svg
                  viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                  className="block h-auto w-full min-w-0"
                  role="img"
                  aria-labelledby="growth-chart-title growth-chart-desc"
                >
                  <title id="growth-chart-title">{chartTitle(region)}</title>
                  <desc id="growth-chart-desc">Cumulative count from {HUMANOID_GROWTH_START_YEAR} through {HUMANOID_GROWTH_END_YEAR}, ending at {total} dated company records.</desc>
                  <defs>
                    <linearGradient id="growthLine" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#42E8FF" />
                      <stop offset="55%" stopColor="#3B82F6" />
                      <stop offset="100%" stopColor="#A855F7" />
                    </linearGradient>
                    <filter id="growthGlow" x="-30%" y="-30%" width="160%" height="160%">
                      <feGaussianBlur stdDeviation="5" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <radialGradient id="growthBg" cx="60%" cy="45%" r="70%">
                      <stop offset="0%" stopColor="#17345d" stopOpacity="0.24" />
                      <stop offset="100%" stopColor="#07101f" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  <rect width={WIDTH} height={HEIGHT} fill="#07101f" />
                  <rect width={WIDTH} height={HEIGHT} fill="url(#growthBg)" />

                  {yTicks.map((tick) => {
                    const y = PAD.top + plotHeight - (tick / yScaleMax) * plotHeight;
                    return (
                      <g key={`y-${tick}`}>
                        <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y} y2={y} stroke="rgba(148,163,184,.13)" strokeWidth="1" />
                        <text x={PAD.left - 16} y={y + 4} textAnchor="end" fill="#64748b" fontSize="12">{tick}</text>
                      </g>
                    );
                  })}

                  {yearLabels.map((year) => {
                    const x = PAD.left + ((year - HUMANOID_GROWTH_START_YEAR) / (HUMANOID_GROWTH_END_YEAR - HUMANOID_GROWTH_START_YEAR)) * plotWidth;
                    return (
                      <g key={year}>
                        <line x1={x} x2={x} y1={PAD.top} y2={HEIGHT - PAD.bottom} stroke="rgba(100,150,255,.06)" strokeWidth="1" />
                        <text x={x} y={HEIGHT - 27} textAnchor="middle" fill="#64748b" fontSize="12">{year}</text>
                      </g>
                    );
                  })}

                  <text x={WIDTH * 0.53} y={HEIGHT * 0.70} textAnchor="middle" fill="rgba(219,234,254,.12)" fontSize="29" fontWeight="800" letterSpacing="1.5">@techniahqrobot</text>

                  <path
                    key={`glow-${region}`}
                    d={pathD}
                    fill="none"
                    stroke="url(#growthLine)"
                    strokeWidth="12"
                    strokeLinecap="round"
                    opacity="0.13"
                    filter="url(#growthGlow)"
                    pathLength="1"
                    style={reduceMotion ? undefined : { strokeDasharray: 1, animation: "growth-line-draw 1.9s cubic-bezier(.2,.8,.2,1) both" }}
                  />
                  <path
                    key={`line-${region}`}
                    d={pathD}
                    fill="none"
                    stroke="url(#growthLine)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    pathLength="1"
                    style={reduceMotion ? undefined : { strokeDasharray: 1, animation: "growth-line-draw 1.9s cubic-bezier(.2,.8,.2,1) both" }}
                  />

                  {points.map((point, index) => {
                    const hasNew = point.founded.length > 0;
                    const isLast = index === points.length - 1;
                    return (
                      <g
                        key={`${region}-${point.year}`}
                        role="button"
                        tabIndex="0"
                        aria-label={`${point.year} ${point.total} cumulative companies. ${hasNew ? `${point.founded.length} founded this year.` : "No dated record in this collection."}`}
                        onMouseEnter={() => setActiveYear(point.year)}
                        onMouseLeave={() => setActiveYear(null)}
                        onFocus={() => setActiveYear(point.year)}
                        onBlur={() => setActiveYear(null)}
                        onClick={(event) => {
                          event.stopPropagation();
                          setActiveYear((current) => (current === point.year ? null : point.year));
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setActiveYear(point.year);
                          }
                        }}
                        style={reduceMotion ? undefined : { transformOrigin: `${point.x}px ${point.y}px`, animation: `growth-point-in .25s ease-out ${index * 0.045}s both` }}
                      >
                        {isLast && !reduceMotion && <circle cx={point.x} cy={point.y} r="11" fill="none" stroke="#A855F7" strokeWidth="2" style={{ transformOrigin: `${point.x}px ${point.y}px`, animation: "growth-pulse 1.7s ease-in-out 2.1s infinite" }} />}
                        <circle cx={point.x} cy={point.y} r={hasNew ? 6 : 3.2} fill={hasNew ? "#DDFBFF" : "#334155"} stroke={hasNew ? "#42E8FF" : "#475569"} strokeWidth={hasNew ? 3 : 1.5} />
                        <circle cx={point.x} cy={point.y} r="13" fill="transparent" />
                      </g>
                    );
                  })}
                </svg>

                {selectedPoint && (
                  <div
                    className="pointer-events-none absolute z-10 w-[min(280px,82vw)] border border-cyan-300/25 bg-[#07101f]/95 p-4 shadow-2xl backdrop-blur"
                    style={{
                      left: `${clamp((selectedPoint.x / WIDTH) * 100, 18, 82)}%`,
                      top: `${clamp((selectedPoint.y / HEIGHT) * 100, 12, 68)}%`,
                      transform: "translate(-50%, -105%)",
                    }}
                  >
                    <p className="text-sm font-black text-white">{selectedPoint.year} · {selectedPoint.total}</p>
                    <p className="mt-1 text-[11px] font-black uppercase tracking-[0.14em] text-cyan-300">Founded that year</p>
                    <div className="mt-2 space-y-2 text-xs">
                      {selectedPoint.founded.length ? selectedPoint.founded.map((company) => (
                        <div key={company.id}>
                          <p className="font-bold text-slate-100">{company.company}</p>
                          <p className="text-slate-500">{company.country}</p>
                        </div>
                      )) : <p className="text-slate-400">No dated record in this collection</p>}
                    </div>
                    <p className="mt-3 border-t border-white/10 pt-2 text-xs text-slate-400">Cumulative total {selectedPoint.total}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-8">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">2026 SNAPSHOT</p>
              <h2 className="mt-2 text-2xl font-black">Dated company records by region</h2>
            </div>
            <p className="text-xs text-slate-500">Generated from the same company records as the chart</p>
          </div>
          <div className="mt-4 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
            {Object.entries(totals).map(([name, value]) => (
              <button
                key={name}
                type="button"
                onClick={() => {
                  setRegion(name);
                  window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
                }}
                className="bg-[#07101f] p-5 text-left transition hover:bg-[#0B1428] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-300"
              >
                <p className="text-4xl font-black tabular-nums">{value}</p>
                <p className="mt-2 text-xs font-black uppercase tracking-[0.14em] text-slate-400">{name}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="mt-8 border border-white/10 bg-[#07101f] p-5">
          <h2 className="text-xl font-black">Records awaiting a documented founding year</h2>
          <p className="mt-3 text-sm leading-7 text-slate-400">These companies remain in the collection, but their dates are excluded from the chart. The inspected source did not establish a founding year or could not be retrieved.</p>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {regionCompanies.filter((company) => !Number.isFinite(company.foundedYear)).map((company) => <li key={company.id} className="text-sm"><a href={company.sourceUrl} target="_blank" rel="noreferrer" className="font-bold text-cyan-300 underline">{company.company}</a><span className="text-slate-400"> — {company.robotName}; year not confirmed</span></li>)}
          </ul>
        </section>

        <section className="mt-10 grid gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-violet-300">FOUNDING RECORDS</p>
            <h2 className="mt-2 text-2xl font-black">{region} company timeline</h2>
            <div className="mt-5 space-y-4">
              {series.filter((item) => item.founded.length > 0).map((item) => (
                <article key={item.year} className="border border-white/10 bg-[#07101f] p-5">
                  <h3 className="text-lg font-black">{item.year} — company founding records</h3>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {item.founded.map((company) => (
                      <div key={company.id} className="border-l-2 border-cyan-300/45 pl-4">
                        <p className="font-black text-white">{company.company}</p>
                        <p className="mt-1 text-sm text-slate-400">Founded {company.foundedYear} · {company.country}</p>
                        <p className="mt-1 text-xs text-slate-500">{company.robotName}</p>
                        <a href={company.sourceUrl} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-bold text-cyan-300 underline">{company.sourceName}</a>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>

          <aside className="space-y-4">
            <article className="border border-white/10 bg-[#0B1428] p-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">METHODOLOGY</p>
              <p className="mt-3 text-sm leading-7 text-slate-300">
                The collection includes companies associated with complete bipedal, wheeled or social humanoid platforms. Nineteen records have a documented founding year; ten remain listed separately without a confirmed date. Sources include company histories and releases, a university profile and an explicitly identified historical press report.
              </p>
              <p className="mt-3 text-sm leading-7 text-slate-400">
                A company remains part of a historical founding count after acquisition or closure. Regions are catalogue groupings, not an assertion of current headquarters. Coverage gaps and the exclusion of companies founded before 2000 prevent comparisons of national market size or growth.
              </p>
            </article>
            <article className="border border-white/10 bg-[#07101f] p-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-violet-300">SOURCES</p>
              <p className="mt-3 text-sm leading-7 text-slate-400">Each dated record links to the source used for its year. A homepage or a copyright date alone is insufficient to establish a founding year. No incorporation-register audit or independent census is claimed.</p>
              <p className="mt-3 text-xs text-slate-500">Reviewed September 29, 2026 · {humanoidCompanyGrowthCompanies.length} records; 19 dated</p>
            </article>
            <article className="border border-white/10 bg-[#07101f] p-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">RELATED DATA</p>
              <div className="mt-3 flex flex-col gap-2 text-sm font-bold">
                <Link to="/humanoid-intelligence" className="text-cyan-300 hover:text-cyan-100">Humanoid Intelligence Database →</Link>
                <Link to="/humanoid-robots/database" className="text-cyan-300 hover:text-cyan-100">Humanoid Robot Database →</Link>
                <Link to="/robotics-companies" className="text-cyan-300 hover:text-cyan-100">Robotics Companies →</Link>
              </div>
            </article>
          </aside>
        </section>
      </section>
    </div>
  );
}
